// geminiRecipeService.js - Gemini Multimodal OCR & Recipe Parser
import {
  toTitleCase,
  getStandardizedUnit,
  parseFraction,
  STANDARD_CUISINES
} from "./recipeStandardization";

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || "";
const GEMINI_PRIMARY_MODEL = "gemini-3.5-flash";
const GEMINI_FALLBACK_MODEL = "gemini-3.1-flash-lite";

const SYSTEM_RECIPE_PROMPT = `
You are an expert culinary AI for the Astrup Family Cookbook.
Analyze the user's input (which may be a photo of a handwritten recipe card, a magazine page, a typed recipe, or a casual brief description).
Extract and structure the recipe into this EXACT JSON format:
{
  "recipe_name": "Title of Recipe",
  "description": "Appetizing 1-2 sentence description",
  "prep_time": 15,
  "cook_time": 30,
  "servings": 4,
  "cuisine": "American",
  "difficulty": "Easy",
  "ingredients": [
    {
      "amount": "1",
      "measurement_name": "cup",
      "ingredient_name": "All-Purpose Flour"
    }
  ],
  "steps": [
    "Preheat the oven to 375°F and grease a 9x13 inch baking dish.",
    "Whisk dry ingredients together in a large bowl."
  ],
  "tags": ["Baking", "Comfort Food"]
}

Guidelines:
1. Cuisine should be one of: American, Italian, Mexican, Asian, Mediterranean, French, BBQ & Grilling, Breakfast & Brunch, Dessert & Baking, Seafood, Soups & Stews, Indian, Thai, Japanese, Chinese, Greek, Latin American, Middle Eastern, Southern & Comfort, Vegetarian & Vegan, Healthy & Light, European, General.
2. Difficulty should be: Easy, Medium, or Hard.
3. Ingredients: Clean units into standard abbreviations where possible (tsp, tbsp, cup, oz, fl oz, lb, gram, ml, clove, slice, can, etc.).
4. Return ONLY raw valid JSON. Do not wrap in markdown quotes if possible, or use standard markdown JSON code fence.
`;

export const cleanGeminiJson = (text) => {
  if (!text) return null;
  let clean = text.trim();
  // Strip markdown code fences if present
  clean = clean.replace(/^```json\s*/i, "").replace(/^```\s*/i, "").replace(/```$/i, "").trim();
  return clean;
};

// Convert a File object to base64
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // Strip "data:*/*;base64," prefix
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

/**
 * Parses a recipe from text prompt, image file, or both using Gemini 3.5 Flash
 * @param {Object} options
 * @param {string} [options.promptText] User prompt or rough recipe text
 * @param {File} [options.imageFile] Image file from camera or device (handwritten card, cookbook photo)
 * @returns {Promise<Object>} Normalized recipe object ready for CreateRecipe state
 */
export const extractRecipeWithGemini = async ({ promptText = "", imageFile = null }) => {
  if (!GEMINI_API_KEY) {
    throw new Error("Gemini API key is not configured. Please add REACT_APP_GEMINI_API_KEY.");
  }

  const parts = [];

  // Add system guidance and user prompt
  let fullPrompt = SYSTEM_RECIPE_PROMPT;
  if (promptText.trim()) {
    fullPrompt += `\n\nUser Input / Recipe Text:\n${promptText.trim()}`;
  }
  parts.push({ text: fullPrompt });

  // Add image if present
  if (imageFile) {
    const base64Data = await fileToBase64(imageFile);
    parts.push({
      inlineData: {
        mimeType: imageFile.type || "image/jpeg",
        data: base64Data
      }
    });
  }

  const payload = {
    contents: [{ parts }]
  };

  // Try primary model, fallback to fallback model on failure
  let responseText = "";
  try {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_PRIMARY_MODEL}:generateContent?key=${GEMINI_API_KEY}`;
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    if (!resp.ok) throw new Error(`HTTP error ${resp.status}`);
    const data = await resp.json();
    responseText = data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
  } catch (err) {
    console.warn(`Primary Gemini model (${GEMINI_PRIMARY_MODEL}) failed, trying fallback (${GEMINI_FALLBACK_MODEL}):`, err);
    const fallbackUrl = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_FALLBACK_MODEL}:generateContent?key=${GEMINI_API_KEY}`;
    const fallbackResp = await fetch(fallbackUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    if (!fallbackResp.ok) throw new Error(`Fallback HTTP error ${fallbackResp.status}`);
    const fallbackData = await fallbackResp.json();
    responseText = fallbackData?.candidates?.[0]?.content?.parts?.[0]?.text || "";
  }

  const cleanedJson = cleanGeminiJson(responseText);
  if (!cleanedJson) {
    throw new Error("Gemini returned an empty response.");
  }

  let parsed = null;
  try {
    parsed = JSON.parse(cleanedJson);
  } catch (e) {
    console.error("JSON parse error from Gemini output:", cleanedJson);
    throw new Error("Could not parse recipe details from AI. Please try again with clearer text or photo.");
  }

  // Normalize parsed output to match CreateRecipe form state
  const rawIngredients = parsed.ingredients || parsed.recipe?.ingredients || [];
  const normalizedIngredients = rawIngredients.map(ing => {
    const rawAmt = ing.amount || ing.quantity || "";
    const rawUnit = ing.measurement_name || ing.unit || ing.measurement_unit || "";
    const rawName = ing.ingredient_name || ing.name || ing.item || "";
    return {
      amount: rawAmt ? parseFraction(rawAmt) : "",
      measurement_name: rawUnit ? getStandardizedUnit(rawUnit) : "",
      ingredient_name: rawName ? toTitleCase(rawName) : ""
    };
  });

  const rawSteps = parsed.steps || parsed.instructions || parsed.recipe?.steps || [];
  const normalizedSteps = rawSteps.map((st, idx) => ({
    id: Date.now() + idx,
    step_number: idx + 1,
    instruction: typeof st === "string" ? st.trim() : (st.instruction || st.text || "")
  }));

  const cuisineMatch = STANDARD_CUISINES.find(
    c => c.toLowerCase() === (parsed.cuisine || "").toLowerCase()
  ) || "American";

  return {
    recipe_name: toTitleCase(parsed.recipe_name || parsed.title || parsed.recipe?.name || "Untitled Recipe"),
    description: parsed.description || "",
    cuisine: cuisineMatch,
    prep_time: parsed.prep_time || parsed.prepTime || 15,
    cook_time: parsed.cook_time || parsed.cookTime || 30,
    servings: String(parsed.servings || 4),
    difficulty: parsed.difficulty || "Medium",
    ingredients: normalizedIngredients.length > 0 ? normalizedIngredients : [{ ingredient_name: "", amount: "", measurement_name: "" }],
    steps: normalizedSteps.length > 0 ? normalizedSteps : [{ id: Date.now(), step_number: 1, instruction: "" }],
    tags: Array.isArray(parsed.tags) ? parsed.tags : [],
    images: []
  };
};
